import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import withStyles from '@material-ui/core/styles/withStyles';
import Typography from '@material-ui/core/Typography';
import Button from '@material-ui/core/Button';
import classNames from 'classnames';
import isArray from 'lodash/isArray';
import isEmpty from 'lodash/isEmpty';
import { keyEmptyCheck } from 'aurora-utils/utils';
import { ADGMModal, Loading, FilterButtons, ADGMImage } from 'aurora-components';

import Filter from '../Filter';
import { FIELD_TYPES, FILTERS_DATA, FULL_WIDTH_FILTERS, getFilterData, isEmptyObject } from '../constants';

import styles from './styles';

const ADGMFiltersModal = ({
  classes,
  loading,
  openModal,
  formData,
  filters,
  appliedFilters,
  propertyFilterOptions,
  selectedPropertyFilter,
  dropDowns,
  handleChange,
  propertyFiltersHandler,
  filtersResetHandler,
  searchHandler,
  closeModalHandler,
  customClasses,
  filterModalTitle,
  showClearAll,
  filterBtnTitle,
  handlePrefixChange,
  isPMABank,
}) => {
  const structuredFilters = useMemo(() => {
    const data = [];
    let i = 0;
    while (i < filters?.length) {
      const filterName = filters[i];
      const type = FILTERS_DATA[filterName]?.type;
      if (type === FIELD_TYPES?.RADIO || FULL_WIDTH_FILTERS.includes(filterName)) {
        data.push(filterName);
        i += 1;
      } else {
        const groupedData = [filterName];
        let j = i + 1;
        let currentFilterType = FILTERS_DATA[filters[j]]?.type;
        while (
          j < filters?.length &&
          (currentFilterType !== FIELD_TYPES?.RADIO || FULL_WIDTH_FILTERS.includes(filterName))
        ) {
          groupedData.push(filters[j]);
          currentFilterType = FILTERS_DATA[filters[j + 1]]?.type;
          j += 1;
        }
        data.push(groupedData);
        i = j;
      }
    }

    return data;
  }, [filters]);
  const iOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
  const handleSearch = () => {
    closeModalHandler();
    searchHandler();
  };

  const handleSwitch = id => {
    closeModalHandler();
    propertyFiltersHandler(id);
  };

  const generateFilter = filterName => {
    const { renderCondition = true, type, ...filterData } = getFilterData(filterName, formData, dropDowns);
    return renderCondition ? (
      <div
        className={classNames(classes.filter, {
          [classes.fullWidthFilter]: type === FIELD_TYPES.RADIO || FULL_WIDTH_FILTERS.includes(filterName),
        })}
      >
        <Filter
          name={filterName}
          type={type}
          data={filterData}
          handleChange={handleChange}
          showDropdownAsPrefix={filterName === 'certificateNo'}
          dropDownOptions={filterData}
          onPrefixChange={handlePrefixChange}
        />
      </div>
    ) : null;
  };

  const renderFilters = filtersData => {
    return filtersData?.map(filterItem => {
      if (!isArray(filterItem)) {
        const filterComponent = generateFilter(filterItem);
        return filterComponent ? (
          <div key={filterItem} className={classes.filterContainer}>
            {filterComponent}
          </div>
        ) : null;
      }

      if (isEmpty(filterItem)) return null;

      const groupedFiltersList = [];
      const perRowFilters = 2;
      for (let i = 0; i < filterItem.length; i += perRowFilters) {
        const chunk = filterItem.slice(i, i + perRowFilters);
        groupedFiltersList.push(chunk);
      }
      return groupedFiltersList?.map(filtersGroup => {
        const filterComponentsList = filtersGroup?.map(filter => generateFilter(filter));
        const isEmptyList = filterComponentsList.every(filterComponent => filterComponent === null);
        return !isEmptyList ? (
          <div key={filterComponentsList?.join(',')} className={classes.filterContainer}>
            {filterComponentsList}
          </div>
        ) : null;
      });
    });
  };

  const disabledBtn = useMemo(
    () =>
      isPMABank
        ? (!formData?.plotNumber &&
            !formData?.unitNumber &&
            !formData?.buildingRegNumber &&
            !formData?.unitRegNo &&
            !formData?.premiseId) === true && (!formData?.district || !formData?.community) === true
        : isEmpty(appliedFilters) && isEmptyObject(formData),
    [formData, isPMABank],
  );

  return (
    <ADGMModal
      className={classNames([classes.filtersModal, iOS && classes.paddingIOS, customClasses?.filtersModal])}
      open={openModal}
      onClose={closeModalHandler}
      disableScrollLock={false}
    >
      {loading && <Loading />}
      <div className={classes.titleContainer}>
        <Typography variant='h5' className={classes.title}>
          {keyEmptyCheck(filterModalTitle)}
        </Typography>
        <Button variant='contained' className={classes.closeBtn} onClick={closeModalHandler}>
          <ADGMImage name='close.svg' alt='close' />
        </Button>
      </div>
      <div className={classes.filtersList}>
        {!isEmpty(propertyFilterOptions) && (
          <div className={classes.propertyFiltersWrapper}>
            <FilterButtons
              selected={selectedPropertyFilter}
              options={propertyFilterOptions}
              onSwitchHandler={handleSwitch}
            />
          </div>
        )}
        {renderFilters(structuredFilters)}
      </div>
      <div className={classes.filtersModalFooter}>
        {showClearAll && (
          <Button variant='contained' className={classes.resetBtn} onClick={filtersResetHandler} disabled={disabledBtn}>
            {keyEmptyCheck('reset_filters')}
          </Button>
        )}
        <Button variant='contained' className={classes.searchBtn} onClick={handleSearch} disabled={disabledBtn}>
          {keyEmptyCheck(filterBtnTitle)}
        </Button>
      </div>
    </ADGMModal>
  );
};

ADGMFiltersModal.propTypes = {
  classes: PropTypes.object.isRequired,
  loading: PropTypes.bool.isRequired,
  openModal: PropTypes.bool.isRequired,
  propertyFilterOptions: PropTypes.array.isRequired,
  selectedPropertyFilter: PropTypes.string.isRequired,
  formData: PropTypes.object.isRequired,
  filters: PropTypes.array.isRequired,
  appliedFilters: PropTypes.object.isRequired,
  dropDowns: PropTypes.object.isRequired,
  handleChange: PropTypes.func.isRequired,
  propertyFiltersHandler: PropTypes.func.isRequired,
  filtersResetHandler: PropTypes.func.isRequired,
  searchHandler: PropTypes.func.isRequired,
  closeModalHandler: PropTypes.func.isRequired,
  customClasses: PropTypes.object.isRequired,
  filterModalTitle: PropTypes.string.isRequired,
  handlePrefixChange: PropTypes.func,
  showClearAll: PropTypes.bool,
  filterBtnTitle: PropTypes.string,
  isPMABank: PropTypes.bool,
};

ADGMFiltersModal.defaultProps = {
  handlePrefixChange: () => {},
  showClearAll: true,
  filterBtnTitle: 'search',
  isPMABank: false,
};
export default withStyles(styles)(ADGMFiltersModal);
